/**
 * THE QUESTION "ASK ABOUT THIS SCREEN" SENDS, SCREEN BY SCREEN.
 *
 * ⚠️ ITS READER MOVED, AND THE MAP DID NOT (2026-09-15). This was the question the per-screen
 * "?" pre-filled the chat composer with; the "?" now starts that screen's walkthrough directly
 * and asks nothing. The question is still worth having, because a tour answers "where is the
 * control" and cannot answer "so why is mine greyed out" — so it is offered ONCE, by the
 * button `WalkthroughOverlay` shows when a tour ends, which is exactly when somebody has the
 * second question. It is read off the path the user ENDED UP on, not the key the tour started
 * from: a tour can move them, and the button says "this screen".
 *
 * The value of the map is unchanged either way: the user did not have to work out how to
 * phrase it, and the wording matters more than it looks.
 *
 * WHY THE WORDING IS COPIED FROM THE CORPUS
 * -----------------------------------------
 * The Function App answers from a curated guide whose entries are `### question` headings
 * (`Support/csm-user-guide.md`), and the model matches against those headings. Asking a
 * question in the corpus's own words is therefore the difference between the curated answer
 * and a near-miss — and a near-miss on this path is expensive, because the assistant no longer
 * investigates anything: a question the guide does not cover comes back as an escalation offer
 * rather than an answer. Every question below that has a matching entry is that entry's
 * heading, character for character.
 *
 * Where the corpus has no entry, the question is written plainly, in the same voice, and left
 * to be covered. Until an entry lands, the assistant says it does not have an answer yet and
 * offers to hand over to support, which is the correct behaviour and not a failure of this file.
 * (`choose-organization` is deliberately absent: it is not a screen CoSMo may talk about.)
 *
 * ⚠️ EVERY SCREEN IN `routes.ts` MUST HAVE AN ENTRY, and it is still a requirement rather than
 * a convenience: a tour can finish on any screen, so a hole here is a handover the user simply
 * does not get, on a screen nobody chose. `screenHelp.test.ts` fails the build when a screen is
 * added without one. Access is NOT decided here — `screenForPath` plus `canReachScreen` decide
 * what is offered, and this file only supplies the words.
 */

import { screenForPath } from "./routes";

export interface ScreenHelp {
  /** The screen the question is about. Always a key from `routes.ts`. */
  readonly screenKey: string;

  /** What "Ask about this screen" asks on the user's behalf. */
  readonly question: string;
}

/**
 * `/home` is three screens wearing one key.
 *
 * `Home.tsx` renders the Capacity Manager, the write-back approval board or the shop-floor
 * Kanban depending on `isManagerMode` and `isActiveJobMode`. The screen table has one row for
 * it because the ROUTE is one route, and splitting it would mean two rows with the same path,
 * which `screenForPath` could not tell apart.
 *
 * Only the manager flag is taken here, because that is the split the user experiences: Shop
 * Floor is the operator's board, Manager is the planning side. The second flag (Active jobs
 * versus Completed jobs) is a toggle ON the manager screen rather than a different place, and
 * the corpus entry for Active jobs opens by explaining that toggle — so a manager who asked
 * while looking at the approval board still gets an answer that starts in the right place.
 *
 * Adding a third input here would also put a second, differently-shaped truth table in front
 * of a feature whose whole point is that it is always there.
 */
const HOME_SHOPFLOOR = "What am I looking at on the Shop Floor board?";
const HOME_MANAGER = "What is the Active Jobs screen?";

const QUESTIONS: Readonly<Record<string, string>> = Object.freeze({
  // ---- Corpus entries, quoted exactly ---------------------------------------------------
  home: HOME_SHOPFLOOR, // replaced by mode below; present so the exhaustiveness test can see it
  job: "What is on the production order details screen?",
  "job-routing": "What do the Resources and Logging sections show?",
  "job-bom": "What do the Components columns mean?",
  "job-images": "How do I add a photo or a document to an order?",
  images: "Where do I see all the images and documents?",
  "settings-system": "How do I get to System Settings, and what is on each tab?",
  "settings-users": "How do I create a user?",
  "settings-operations": "How do I create a resource?",
  analytics: "How do I build a report?",
  hierarchical: "How do I see which orders are linked to one another?",

  // ---- No corpus entry yet. Plain questions in the same voice. --------------------------
  // `job-images` above takes the corpus entry because it covers photographs specifically;
  // this one asks the documents half of the same job in its own words rather than sending
  // two screens to one heading, which would make one of them the wrong answer.
  "job-documents": "How do I add and open documents on a production order?",
  "similar-jobs-images": "How do I see images from similar production orders?",
  documents: "How do I find and open a document on the Documents screen?",

  // ---- Tools and Toolsets ---------------------------------------------------------------
  // The guide's entry for this screen is written under this exact heading. Change one and
  // change the other, character for character.
  "settings-toolsets": "How do I add a tool and put it in a toolset?",
});

/**
 * What should "Ask about this screen" ask, here?
 *
 * Returns null for any path the screen table does not map — `/login`, `/logout`,
 * `termsandconditions`, `consent-details`, `/diagnostic` — which is the same set the
 * assistant declines to describe anywhere else. Null means "render no button", never "render
 * a button that asks nothing".
 *
 * `isManagerMode` is a VIEW flag (`appSettings.isManagerMode`), not a permission. It is read
 * only to choose between the two `/home` screens; it can never widen or narrow what the user
 * is offered, which is `canReachScreen`'s job alone.
 */
export const screenHelpFor = (
  pathname: string,
  isManagerMode: boolean
): ScreenHelp | null => {
  const screen = screenForPath(pathname);
  if (!screen) return null;

  if (screen.key === "home") {
    return { screenKey: "home", question: isManagerMode ? HOME_MANAGER : HOME_SHOPFLOOR };
  }

  const question = QUESTIONS[screen.key];
  // A screen with no entry is a build error caught by `screenHelp.test.ts`, not something to
  // paper over at runtime with a generated question — "What is this screen?" would reach the
  // model as a question the guide has no heading for and come straight back as an escalation.
  if (!question) return null;

  return { screenKey: screen.key, question };
};

/**
 * Every screen key this file has a question for. Exported for the exhaustiveness test, which
 * compares it against `SCREEN_KEYS` in both directions — a question for a screen that no
 * longer exists is dead weight the same way a missing one is a hole.
 */
export const SCREEN_HELP_KEYS: readonly string[] = Object.freeze(Object.keys(QUESTIONS));
